import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, FloppyDisk, Eye } from "@phosphor-icons/react";

const EMPTY = { name: "", category: "general", subject: "", html_body: "", text_body: "", tags: [] };

export default function TemplateEditor() {
    const { id } = useParams();
    const isNew = !id || id === "new";
    const nav = useNavigate();
    const [t, setT] = useState(EMPTY);
    const [previewMode, setPreviewMode] = useState(false);
    const [isSystem, setIsSystem] = useState(false);

    useEffect(() => {
        document.title = `Surveys2CAD | ${isNew ? "New Template" : "Edit Template"}`;
        if (!isNew) {
            api.get(`/admin/templates/${id}`).then((r) => {
                setT(r.data);
                setIsSystem(!!r.data.is_system);
            }).catch(() => toast.error("Template not found"));
        }
    }, [id, isNew]);

    const save = async (e) => {
        e?.preventDefault();
        try {
            const payload = { ...t, tags: typeof t.tags === "string" ? t.tags.split(",").map((s) => s.trim()).filter(Boolean) : t.tags };
            if (isNew) {
                const { data } = await api.post("/admin/templates", payload);
                toast.success("Created");
                nav(`/admin/templates/${data.id}`);
            } else {
                await api.put(`/admin/templates/${id}`, payload);
                toast.success("Saved");
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        }
    };

    return (
        <div className="p-4 sm:p-6 lg:p-8" data-testid="template-editor">
            <button onClick={() => nav("/admin/templates")} className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-cyan-300 mb-4">
                <ArrowLeft size={16} /> Templates
            </button>
            <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
                <div>
                    <div className="overline mb-2">{isSystem ? "System template" : "Editor"}</div>
                    <h1 className="font-heading text-3xl font-bold text-slate-50 tracking-tight">
                        {isNew ? "New email template" : t.name || "Template"}
                    </h1>
                </div>
                <div className="flex gap-2">
                    <button onClick={() => setPreviewMode(!previewMode)} className="btn-ghost" data-testid="tpl-toggle-preview">
                        <Eye size={16} /> {previewMode ? "Edit" : "Preview"}
                    </button>
                    {!isSystem && (
                        <button onClick={save} className="btn-primary" data-testid="tpl-save-btn">
                            <FloppyDisk size={16} weight="bold" /> {isNew ? "Create" : "Save"}
                        </button>
                    )}
                </div>
            </div>

            {isSystem && (
                <div className="tech-card p-4 mb-6 border-orange-500/30 bg-orange-500/5">
                    <p className="text-sm text-orange-200">This is a system template — protected from edits and deletion. Duplicate it to customize.</p>
                </div>
            )}

            {previewMode ? (
                <div className="tech-card p-0 overflow-hidden">
                    <div className="p-4 border-b border-slate-800 bg-slate-900/50">
                        <div className="overline">Subject</div>
                        <div className="text-slate-100 text-base mt-0.5">{t.subject || "(no subject)"}</div>
                    </div>
                    <iframe title="preview" srcDoc={t.html_body} sandbox="" className="w-full min-h-[65vh] bg-white" />
                </div>
            ) : (
                <form onSubmit={save} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2 tech-card p-6 space-y-4">
                        <div>
                            <label className="overline mb-2 block">Name</label>
                            <input type="text" required value={t.name} onChange={(e) => setT({ ...t, name: e.target.value })} disabled={isSystem}
                                className="w-full bg-slate-900/70 border border-slate-700 rounded-lg px-3 py-2.5 text-slate-100 focus:outline-none focus:border-cyan-500 disabled:opacity-60"
                                data-testid="tpl-name" />
                        </div>
                        <div>
                            <label className="overline mb-2 block">Subject line</label>
                            <input type="text" required value={t.subject} onChange={(e) => setT({ ...t, subject: e.target.value })} disabled={isSystem}
                                placeholder="Supports merge tags like {{name}}"
                                className="w-full bg-slate-900/70 border border-slate-700 rounded-lg px-3 py-2.5 text-slate-100 focus:outline-none focus:border-cyan-500 disabled:opacity-60"
                                data-testid="tpl-subject" />
                        </div>
                        <div>
                            <label className="overline mb-2 block">HTML body</label>
                            <textarea rows={18} required value={t.html_body} onChange={(e) => setT({ ...t, html_body: e.target.value })} disabled={isSystem}
                                className="w-full bg-slate-900/70 border border-slate-700 rounded-lg px-3 py-2.5 text-slate-100 focus:outline-none focus:border-cyan-500 font-mono-alt text-xs resize-y disabled:opacity-60"
                                data-testid="tpl-html" />
                            <p className="text-[11px] text-slate-500 mt-1">Merge tags: <code className="text-cyan-300">{`{{name}}`}</code>, <code className="text-cyan-300">{`{{public_base_url}}`}</code>, <code className="text-cyan-300">{`{{unsubscribe_url}}`}</code></p>
                        </div>
                        <div>
                            <label className="overline mb-2 block">Plain-text body (optional)</label>
                            <textarea rows={5} value={t.text_body || ""} onChange={(e) => setT({ ...t, text_body: e.target.value })} disabled={isSystem}
                                className="w-full bg-slate-900/70 border border-slate-700 rounded-lg px-3 py-2.5 text-slate-100 focus:outline-none focus:border-cyan-500 resize-y disabled:opacity-60" />
                        </div>
                    </div>
                    <div className="tech-card p-6 space-y-4 h-fit">
                        <div>
                            <label className="overline mb-2 block">Category</label>
                            <select value={t.category} onChange={(e) => setT({ ...t, category: e.target.value })} disabled={isSystem}
                                className="w-full bg-slate-900/70 border border-slate-700 rounded-lg px-3 py-2.5 text-slate-100 focus:outline-none focus:border-cyan-500 disabled:opacity-60"
                                data-testid="tpl-category">
                                {["service", "welcome", "follow-up", "newsletter", "case-study", "promo", "re-engagement", "general"].map((c) => (
                                    <option key={c} value={c}>{c}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="overline mb-2 block">Tags (comma-separated)</label>
                            <input type="text"
                                value={Array.isArray(t.tags) ? t.tags.join(", ") : t.tags || ""}
                                onChange={(e) => setT({ ...t, tags: e.target.value })}
                                disabled={isSystem}
                                className="w-full bg-slate-900/70 border border-slate-700 rounded-lg px-3 py-2.5 text-slate-100 focus:outline-none focus:border-cyan-500 disabled:opacity-60" />
                        </div>
                        <div className="text-xs text-slate-500 leading-relaxed pt-2 border-t border-slate-800">
                            <div className="overline mb-2">Tips</div>
                            Every email auto-appends a tracking pixel, rewrites links for click tracking, and includes an unsubscribe link.
                        </div>
                    </div>
                </form>
            )}
        </div>
    );
}
